import React, {useRef, useEffect, useState} from 'react';
import {Animated, StyleSheet, View, Dimensions, Easing} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import {SkillzText} from '@skillz/ui';
import MatchSelectCell from 'MatchSelectCell';
import PlayableMatchesStore from 'PlayableMatchesStore';
import Tournament from 'SKZTournament';
import CurrentUserStore from 'CurrentUserStore';
import MatchJoinController from 'MatchJoinController';
import NavigationManager from 'NavigationManager';
import ThemeOverrideProps from 'ThemeOverrideProps';
import {_m} from 'INTL';
import ClientPropertyConstants from 'ClientPropertyConstants';
import SkillzImage from 'SkillzImage';
import AWSManager from 'AWSManager';
import {
  STORE_V2_DEP_SUCC_DISC_TPL_CLICK,
  STORE_V2_DEP_SUCC_RECO_TPL_CLICK,
  STORE_V2_DEP_SUCC_DISC_TPL_SEEN,
  STORE_V2_DEP_SUCC_RECO_TPL_SEEN,
} from 'AWSConstants';

const {fontBold} = ThemeOverrideProps.sheetStyles;

type RecommendedTemplateProps = {
  delay?: number;
  onDismiss: () => void;
};

type SendAWSEventType = {tournamentId: string};

const RecommendedTemplate = ({delay = 0, onDismiss}: RecommendedTemplateProps) => {
  const {width} = Dimensions.get('window');
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [isDiscountedTournament, setIsDiscountedTournament] = useState<boolean>(false);
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [isInViewport, setIsInViewport] = useState(false);
  const [showHighlight, setShowHighlight] = useState(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(100)).current;

  const topHighlightX = useRef(new Animated.Value(0)).current;
  const bottomHighlightX = useRef(new Animated.Value(0)).current;
  const topHighlightOpacity = useRef(new Animated.Value(0)).current;
  const bottomHighlightOpacity = useRef(new Animated.Value(0)).current;

  const depositSuccessRecommendedTournament =
    ClientPropertyConstants.getStoreV2DepositSuccessRecommendedTournament();

  const sendTournamentSeenAWSEvent = (tournamentId: string, discounted: boolean) => {
    const eventPayload = {
      data: tournamentId,
    };

    const eventName = discounted
      ? STORE_V2_DEP_SUCC_DISC_TPL_SEEN
      : STORE_V2_DEP_SUCC_RECO_TPL_SEEN;
    AWSManager.trackEvent(eventName, eventPayload);
  };

  useEffect(() => {
    const sectionsData = PlayableMatchesStore.getSectionsData(false);
    if (!sectionsData) return;
    const cashSection = sectionsData.find((section: any) => section.type === 'CASH');
    const discountedTournaments =
      cashSection?.data?.filter(
        (tournament: Tournament) =>
          tournament?.cash_fee_discount && tournament.cash_fee_discount.discounted_cash_fee > 0
      ) ?? [];

    if (discountedTournaments.length > 0) {
      setTournament(discountedTournaments[0]);
      setIsDiscountedTournament(true);
      sendTournamentSeenAWSEvent(discountedTournaments[0].id, true);
    } else {
      setIsDiscountedTournament(false);
      const recommendedSection = sectionsData.find(
        (section: any) => section.type === 'RECOMMENDEDV2'
      );
      if (recommendedSection?.data.length > 0) {
        const recommendedIndex = Math.min(
          depositSuccessRecommendedTournament >= 1 ? depositSuccessRecommendedTournament - 1 : 0,
          recommendedSection.data.length - 1
        );

        setTournament(recommendedSection.data[recommendedIndex]);
        sendTournamentSeenAWSEvent(recommendedSection.data[recommendedIndex].id, false);
      }
    }

    const userData = CurrentUserStore.getCurrentCachedUserData();
    if (userData?.avatar_url) setAvatarUrl(userData.avatar_url);

    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 500,
        delay,
        useNativeDriver: true,
      }),
      Animated.timing(translateY, {
        toValue: 0,
        duration: 500,
        delay,
        useNativeDriver: true,
      }),
    ]).start();

    setTimeout(() => {
      setIsInViewport(true);
      setShowHighlight(true);
    }, 7000);
  }, [fadeAnim, translateY, delay]);

  // Smooth continuous highlight animation
  useEffect(() => {
    if (!showHighlight) return;

    const runAnimation = () => {
      const animateOnce = () => {
        // Step 1: Fade in + move
        Animated.parallel([
          Animated.timing(topHighlightOpacity, {
            toValue: 1,
            duration: 300,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(bottomHighlightOpacity, {
            toValue: 1,
            duration: 300,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(topHighlightX, {
            toValue: width - 165,
            duration: 2000,
            easing: Easing.linear,
            useNativeDriver: true,
          }),
          Animated.timing(bottomHighlightX, {
            toValue: -(width - 165),
            duration: 2000,
            easing: Easing.linear,
            useNativeDriver: true,
          }),
        ]).start(() => {
          // Step 2: Fade out
          Animated.parallel([
            Animated.timing(topHighlightOpacity, {
              toValue: 0,
              duration: 300,
              easing: Easing.inOut(Easing.ease),
              useNativeDriver: true,
            }),
            Animated.timing(bottomHighlightOpacity, {
              toValue: 0,
              duration: 300,
              easing: Easing.inOut(Easing.ease),
              useNativeDriver: true,
            }),
          ]).start(() => {
            // Step 3: Reset positions instantly and rerun
            topHighlightX.setValue(0);
            bottomHighlightX.setValue(0);

            // Small delay before restarting (smooth transition)
            setTimeout(() => {
              animateOnce();
            }, 50);
          });
        });
      };

      // Start loop
      animateOnce();
    };

    runAnimation();

    return () => {
      topHighlightX.stopAnimation();
      bottomHighlightX.stopAnimation();
    };
  }, [showHighlight, width]);

  const sendTournamentClickedAWSEvent = ({tournamentId}: SendAWSEventType) => {
    const eventPayload = {
      data: tournamentId,
    };

    const eventName = isDiscountedTournament
      ? STORE_V2_DEP_SUCC_DISC_TPL_CLICK
      : STORE_V2_DEP_SUCC_RECO_TPL_CLICK;
    AWSManager.trackEvent(eventName, eventPayload);
  };

  const handleTournamentPress = () => {
    if (!tournament) return;
    const navigator = NavigationManager.getNavigator();
    if (!navigator) return;
    sendTournamentClickedAWSEvent({tournamentId: tournament.id});
    onDismiss();
    MatchJoinController.displayJoinFlowForTournamentWithCode(
      tournament,
      navigator,
      null,
      null,
      false,
      undefined,
      false,
      false
    );
  };

  if (!tournament) return null;

  const styles = stylesfn();
  const lineColor = '#FBE039';
  const transparentColor = 'transparent';

  return (
    <Animated.View style={[styles.container, {opacity: fadeAnim, transform: [{translateY}]}]}>
      <View style={styles.dividerContainer}>
        <LinearGradient
          colors={[transparentColor, lineColor]}
          start={{x: 0, y: 0}}
          end={{x: 1, y: 0}}
          style={styles.leftLine}
        />
        <SkillzText style={styles.dividerText}>{_m('Play & win real cash')}</SkillzText>
        <LinearGradient
          colors={[lineColor, transparentColor]}
          start={{x: 0, y: 0}}
          end={{x: 1, y: 0}}
          style={styles.rightLine}
        />
      </View>

      <View style={styles.cellContainer}>
        {/* Top highlight */}
        <Animated.View
          style={[
            styles.templateHighlightTop,
            {transform: [{translateX: topHighlightX}], opacity: topHighlightOpacity},
          ]}
        >
          <SkillzImage
            source={'deposit_recommended_template_highlight_top.png'}
            style={styles.templateHighlightImage}
          />
        </Animated.View>

        {/* Bottom highlight */}
        <Animated.View
          style={[
            styles.templateHighlightBottom,
            {
              transform: [{translateX: bottomHighlightX}, {rotate: '180deg'}],
              opacity: bottomHighlightOpacity,
            },
          ]}
        >
          <SkillzImage
            source={'deposit_recommended_template_highlight_top.png'}
            style={styles.templateHighlightImage}
          />
        </Animated.View>

        <MatchSelectCell
          rowData={tournament}
          avatarUrl={avatarUrl}
          cellIndex={0}
          displayOnly={false}
          fromVSScreen={false}
          hasExternalBanner={false}
          hasMinimumPlayers={true}
          isFriendsTournament={false}
          isSync={false}
          isTournamentCreator={false}
          mainText={tournament.name}
          matchSelectList={null}
          onFriendsTournamentExpired={() => {}}
          onFriendsTournamentShouldReset={() => {}}
          onHighlight={() => {}}
          onPress={handleTournamentPress}
          overtime={false}
          parent={null}
          scaleToContainer={false}
          sectionHeader='Recommended'
          sectionType='CASH'
          setPlayButtonCallback={() => {}}
          setPlayButtonRef={() => {}}
          updatePlayButton={() => {}}
          useSmallCell={false}
          isSyncFTUE={false}
          testIDOverride=''
          shouldShowDiscountAnimation={true}
          isInViewport={isInViewport}
          hasCustomBorder={true}
          customBorderProps={{
            borderWidth: 4,
            borderColor: '#FBE039',
          }}
        />
      </View>
    </Animated.View>
  );
};

const stylesfn = () =>
  StyleSheet.create({
    container: {
      paddingTop: 80,
      overflow: 'visible',
      justifyContent: 'center',
    },
    cellContainer: {
      marginHorizontal: 12,
      overflow: 'visible',
    },
    dividerContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      paddingBottom: 26,
    },
    dividerText: {
      ...fontBold,
      color: '#FBE039',
    },
    leftLine: {
      height: 1,
      flex: 1,
      marginRight: 8,
      marginLeft: 20,
    },
    rightLine: {
      height: 1,
      flex: 1,
      marginLeft: 8,
      marginRight: 20,
    },
    templateHighlightTop: {
      position: 'absolute',
      top: -13,
      left: 8,
      overflow: 'visible',
    },
    templateHighlightBottom: {
      position: 'absolute',
      bottom: -13,
      right: 8,
      overflow: 'visible',
    },
    templateHighlightImage: {
      width: 130,
      height: 20,
    },
  });

export default RecommendedTemplate;
